/**
 * AnalyticsDashboard — admin-only deep analytics tab.
 *
 * Renders 4 stat cards + 4 visualizations (daily line, monthly bar,
 * top salespeople bar, category share donut) plus a top-customers table.
 * Data comes from GET /api/admin/analytics — one round-trip.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api, formatINR } from '../../lib/store';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend,
  CartesianGrid,
} from 'recharts';
import { TrendingUp, FileText, IndianRupee, Truck, Users, Crown, Boxes, GitCompareArrows, ArrowUp, ArrowDown, Minus, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import AnimatedCategoryPie from './AnimatedCategoryPie';

// Olive + gold accent palette — keeps the analytics tab visually
// consistent with the rest of the app's brand.
const COLORS = ['#556B2F', '#DAA520', '#8B4513', '#4A90A4', '#9C27B0', '#2E7D32', '#B93838', '#6B7B5E', '#1F4E79', '#8B6914', '#C9A961'];

// Short rupee formatter for chart axes — shows ₹1.2L, ₹56K etc.
const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

const dayLabel = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

// Pretty "last quote X days ago" badge: shows colour + label based on staleness.
// 0d = green (today), 1-7d = green, 8-21d = amber, 22+ = red, no quotes = grey.
const lastQuoteBadge = (days) => {
  if (days === null || days === undefined) {
    return { label: 'No quotes yet', cls: 'bg-[#E2E6D9] text-[#5C6150] border-[#E2E6D9]' };
  }
  let label;
  if (days === 0) label = 'Today';
  else if (days === 1) label = '1d ago';
  else label = `${days}d ago`;

  let cls;
  if (days <= 7) cls = 'bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30';
  else if (days <= 21) cls = 'bg-[#DAA520]/10 text-[#8B6914] border-[#DAA520]/40';
  else cls = 'bg-[#B93838]/10 text-[#B93838] border-[#B93838]/30';

  return { label, cls };
};

const monthLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [, m] = yyyymm.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[parseInt(m, 10) - 1] || yyyymm;
};

// Full "Month YYYY" label for the comparison card selects
const monthFullLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1] || m} ${y}`;
};

// Percent-change helper — returns `{ pct, dir, label }`.
// dir: 'up' / 'down' / 'flat'. label: "+12%", "-8%", "0%"
const pctChange = (curr, base) => {
  const c = Number(curr) || 0;
  const b = Number(base) || 0;
  if (b === 0) {
    if (c === 0) return { pct: 0, dir: 'flat', label: '0%' };
    return { pct: 100, dir: 'up', label: 'New' };
  }
  const pct = ((c - b) / b) * 100;
  const rounded = Math.abs(pct) < 10 ? pct.toFixed(1) : Math.round(pct);
  if (Math.abs(pct) < 0.05) return { pct: 0, dir: 'flat', label: '0%' };
  return { pct, dir: pct > 0 ? 'up' : 'down', label: `${pct > 0 ? '+' : ''}${rounded}%` };
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="border-[#E2E6D9]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#8F9485] font-medium">{label}</p>
            <p className="text-xl sm:text-2xl font-bold font-mono-data text-[#1A1C16] mt-0.5 truncate">{value}</p>
            {sub && <p className="text-[10px] text-[#5C6150] mt-1">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Date-range filter chips for the analytics header.
// Presets: All, This Week, This Month, Last 30d, Custom. When custom is
// active the two date inputs become editable so the admin can dial in
// any window (e.g. "2 June → 12 June").
export function DateRangeChips({ preset, startDate, endDate, onPreset, onStartDate, onEndDate }) {
  const PRESETS = [
    { key: 'all',    label: 'All Time' },
    { key: 'week',   label: 'This Week' },
    { key: 'month',  label: 'This Month' },
    { key: '30d',    label: 'Last 30d' },
    { key: 'custom', label: 'Custom' },
  ];
  const isCustom = preset === 'custom';
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-2 border border-[#E2E6D9] bg-[#F9FAF7] rounded-lg px-3 py-2"
      data-testid="analytics-date-range"
    >
      <Calendar className="w-4 h-4 text-[#556B2F] flex-shrink-0" />
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => onPreset(p.key)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              preset === p.key
                ? 'bg-[#556B2F] text-white border-[#556B2F]'
                : 'bg-white text-[#5C6150] border-[#E2E6D9] hover:border-[#556B2F] hover:text-[#556B2F]'
            }`}
            data-testid={`analytics-range-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {(isCustom || (startDate && endDate)) && (
        <div className="flex items-center gap-1 text-[11px] sm:text-xs sm:ml-auto flex-wrap">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDate(e.target.value)}
            className="h-7 px-1.5 sm:px-2 border border-[#E2E6D9] rounded bg-white text-[#1A1C16] text-[11px] sm:text-xs min-w-0 max-w-[130px]"
            data-testid="analytics-range-start"
          />
          <span className="text-[#8F9485]">→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDate(e.target.value)}
            className="h-7 px-1.5 sm:px-2 border border-[#E2E6D9] rounded bg-white text-[#1A1C16] text-[11px] sm:text-xs min-w-0 max-w-[130px]"
            data-testid="analytics-range-end"
          />
        </div>
      )}
    </div>
  );
}

// Side-by-side comparison tile for the month-vs-month card.
// Renders: label, two big numbers (A vs B), and a delta chip with arrow.
function CompareTile({ label, valueA, valueB, cmp, testId }) {
  const ArrowIcon = cmp.dir === 'up' ? ArrowUp : cmp.dir === 'down' ? ArrowDown : Minus;
  const deltaColor =
    cmp.dir === 'up'   ? 'bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30' :
    cmp.dir === 'down' ? 'bg-[#B93838]/10 text-[#B93838] border-[#B93838]/30' :
                         'bg-[#E2E6D9] text-[#5C6150] border-[#E2E6D9]';
  return (
    <div className="rounded-lg border border-[#E2E6D9] bg-[#F9FAF7] p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-[#8F9485] font-medium mb-2">{label}</p>
      <div className="flex items-end justify-between gap-2 mb-2">
        <div>
          <p className="text-[10px] text-[#8F9485] uppercase">A</p>
          <p className="text-lg sm:text-xl font-bold font-mono-data text-[#1A1C16]">{valueA}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-[#8F9485] uppercase">B</p>
          <p className="text-base sm:text-lg font-semibold font-mono-data text-[#5C6150]">{valueB}</p>
        </div>
      </div>
      <div className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2 py-0.5 ${deltaColor}`}>
        <ArrowIcon className="w-3 h-3" />
        {cmp.label}
      </div>
    </div>
  );
}

// =====================================================================
// SalespersonBreakdownTable — full breakdown with click-to-expand drill-down.
// Each row can be clicked to fetch that salesperson's recent quotations
// (scoped to the current date range) and show them inline. A "View all"
// link jumps to the Quotation History pre-filtered to the same salesperson
// and date range so admins land where they can act.
// =====================================================================
function SalespersonBreakdownTable({ rows, startDate, endDate }) {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [quoteCache, setQuoteCache] = useState({}); // userId -> {loading, items, total, error}

  const loadQuotes = (userId) => {
    setQuoteCache(c => ({ ...c, [userId]: { loading: true, items: [], total: 0, error: '' } }));
    const params = { limit: 5, page: 1, salesperson_id: userId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getQuotations(params)
      .then(r => {
        setQuoteCache(c => ({ ...c, [userId]: {
          loading: false,
          items: r.data.quotations || [],
          total: r.data.total || 0,
          error: '',
        }}));
      })
      .catch(err => {
        setQuoteCache(c => ({ ...c, [userId]: {
          loading: false, items: [], total: 0,
          error: err.response?.data?.detail || 'Failed to load quotations',
        }}));
      });
  };

  const toggleRow = (userId) => {
    if (expandedId === userId) { setExpandedId(null); return; }
    setExpandedId(userId);
    if (!quoteCache[userId]) loadQuotes(userId);
  };

  const goToHistory = (s, e) => {
    // Uses location.state so QuotationsPage can seed its filter without a
    // reload. Falls back to no-op cleanly if navigation isn't allowed.
    e.stopPropagation();
    navigate('/quotations', {
      state: {
        salesperson_id: s.user_id,
        salesperson_name: s.name,
        start_date: startDate || '',
        end_date: endDate || '',
      }
    });
  };

  return (
    <Card className="border-[#E2E6D9]" data-testid="table-salespeople">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <Users className="w-4 h-4 text-[#556B2F]" /> Full Salesperson Breakdown
          <span className="text-[10px] text-[#8F9485] font-normal ml-1">(click a row for their recent quotes)</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[600px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C6150] border-b border-[#E2E6D9]">
                <th className="py-2 px-3 w-6"></th>
                <th className="py-2 px-3">Salesperson</th>
                <th className="py-2 px-3">Role</th>
                <th className="py-2 px-3">Last Quote</th>
                <th className="py-2 px-3 text-right">Quotations</th>
                <th className="py-2 px-3 text-right">Total Value</th>
                <th className="py-2 px-3 text-right">Avg / Quote</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="7" className="py-6 text-center text-[#8F9485]">No salespeople yet</td></tr>
              ) : rows.map((s) => {
                const badge = lastQuoteBadge(s.days_since_last_quote);
                const isOpen = expandedId === s.user_id;
                const cached = quoteCache[s.user_id];
                return (
                  <React.Fragment key={s.user_id}>
                    <tr
                      className={`border-b border-[#E2E6D9] hover:bg-[#F9FAF7] cursor-pointer ${isOpen ? 'bg-[#FFFBF0]' : ''}`}
                      onClick={() => toggleRow(s.user_id)}
                      data-testid={`salesperson-row-${s.user_id}`}
                    >
                      <td className="py-2 px-3 text-[#8F9485]">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="py-2 px-3">
                        <span className="font-medium text-[#1A1C16]">{s.name}</span>
                        {s.staff_code && <span className="ml-2 text-[10px] font-mono bg-[#556B2F]/10 text-[#556B2F] px-1.5 py-0.5 rounded">{s.staff_code}</span>}
                      </td>
                      <td className="py-2 px-3"><Badge variant="outline" className="capitalize text-[10px]">{s.role || '—'}</Badge></td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-flex items-center text-[10px] font-semibold rounded-full border px-2 py-0.5 ${badge.cls}`}
                          title={s.last_quote_at ? new Date(s.last_quote_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'No quotes yet'}
                          data-testid={`last-quote-${s.user_id}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono-data">{s.count}</td>
                      <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(s.value)}</td>
                      <td className="py-2 px-3 text-right font-mono-data text-[#5C6150]">{s.count > 0 ? formatINR(Math.round(s.value / s.count)) : '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#FFFBF0]" data-testid={`salesperson-drilldown-${s.user_id}`}>
                        <td colSpan="7" className="px-3 py-3">
                          <SalespersonDrilldownPanel
                            salesperson={s}
                            cache={cached}
                            onViewAll={(e) => goToHistory(s, e)}
                            statusCounts={{
                              closed: s.closed_count || 0,
                              pending: s.pending_count || 0,
                              lost: s.lost_count || 0,
                              closed_value: s.closed_value || 0,
                              lost_value: s.lost_value || 0,
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Inline panel rendered under an expanded salesperson row. Shows:
//   • closed/pending/lost badges from the analytics payload
//   • top 5 recent quotations (call to /quotations?salesperson_id=…)
//   • "View all" button → navigates to Quotation History pre-filtered
function SalespersonDrilldownPanel({ salesperson, cache, onViewAll, statusCounts }) {
  const s = salesperson;
  const c = cache || { loading: true, items: [], total: 0 };
  return (
    <div className="space-y-3">
      {/* Status chips (closed/pending/lost) — from analytics payload */}
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1 rounded-full border border-[#2E7D32]/30 bg-[#2E7D32]/10 text-[#2E7D32] px-2 py-0.5">
          <span className="font-semibold">{statusCounts.closed}</span> closed · {formatINR(statusCounts.closed_value)}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-[#F9A825]/40 bg-[#F9A825]/10 text-[#8B6914] px-2 py-0.5">
          <span className="font-semibold">{statusCounts.pending}</span> pending
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-[#C62828]/30 bg-[#C62828]/10 text-[#C62828] px-2 py-0.5">
          <span className="font-semibold">{statusCounts.lost}</span> lost · {formatINR(statusCounts.lost_value)}
        </span>
      </div>

      {/* Recent quotations */}
      <div>
        <p className="text-[10px] uppercase tracking-wide text-[#8F9485] mb-1.5">
          {c.loading ? 'Loading recent quotations…' : `Recent quotations (top ${c.items.length} of ${c.total})`}
        </p>
        {c.loading && <Skeleton className="h-20 w-full" />}
        {c.error && <p className="text-xs text-[#C62828]">{c.error}</p>}
        {!c.loading && !c.error && c.items.length === 0 && (
          <p className="text-xs text-[#8F9485] italic py-3">No quotations by {s.name} in this range.</p>
        )}
        {!c.loading && !c.error && c.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {c.items.map(q => (
              <div
                key={q.id}
                className="border border-[#E2E6D9] rounded-md p-2 bg-white text-xs"
                data-testid={`salesperson-quote-${q.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[#556B2F] font-semibold">{q.quotation_number || q.id?.slice(0, 8)}</span>
                  <StatusPill status={q.order_status} />
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-[#1A1C16] truncate">{q.customer?.name || 'Unknown'}</span>
                  <span className="font-mono-data font-semibold text-[#1A1C16]">{formatINR(q.grand_total)}</span>
                </div>
                <div className="text-[10px] text-[#8F9485] mt-0.5">
                  {q.created_at ? new Date(q.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  {q.customer?.mobile ? ` · ${q.customer.mobile}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {c.total > 0 && (
        <Button
          size="sm"
          variant="outline"
          onClick={onViewAll}
          className="text-xs h-8 border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F] hover:text-white"
          data-testid={`view-all-quotes-${s.user_id}`}
        >
          View all {c.total} quotations by {s.name.split(' ')[0]}
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const st = (status || 'pending').toLowerCase();
  const cls = st === 'closed' ? 'bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30'
             : st === 'lost'   ? 'bg-[#C62828]/10 text-[#C62828] border-[#C62828]/30'
                               : 'bg-[#F9A825]/10 text-[#8B6914] border-[#F9A825]/40';
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide border rounded-full px-1.5 py-0.5 ${cls}`}>
      {st}
    </span>
  );
}


export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Month-compare card state — populated once data lands.
  const [monthA, setMonthA] = useState('');
  const [monthB, setMonthB] = useState('');

  // Date range filter. Chips: 'all' (default), 'week', 'month',
  // '30d', 'custom'. When custom, the two inputs drive startDate/endDate.
  // Passed to /admin/analytics as ISO YYYY-MM-DD strings.
  const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const isoToday = () => new Date().toISOString().slice(0, 10);
  const [rangePreset, setRangePreset] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const applyPreset = (preset) => {
    setRangePreset(preset);
    if (preset === 'all') { setStartDate(''); setEndDate(''); }
    else if (preset === 'week') {
      // Monday-start week (matches Indian business convention).
      const now = new Date();
      const dow = (now.getDay() + 6) % 7; // 0 = Monday
      const monday = new Date(now); monday.setDate(now.getDate() - dow);
      setStartDate(monday.toISOString().slice(0, 10));
      setEndDate(isoToday());
    }
    else if (preset === 'month') {
      const now = new Date();
      setStartDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setEndDate(isoToday());
    }
    else if (preset === '30d') { setStartDate(isoDaysAgo(29)); setEndDate(isoToday()); }
    // 'custom' → leave the two inputs where they are so the user can edit.
    else if (preset === 'custom' && !startDate && !endDate) {
      setStartDate(isoDaysAgo(29));
      setEndDate(isoToday());
    }
  };

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getAdminAnalytics(params)
      .then(res => {
        setData(res.data);
        const m = res.data.monthly || [];
        // Default: latest = A (current month), previous = B
        if (m.length >= 2) {
          setMonthA(m[m.length - 1].month);
          setMonthB(m[m.length - 2].month);
        } else if (m.length === 1) {
          setMonthA(m[0].month);
          setMonthB(m[0].month);
        }
      })
      .catch(err => { setError(err.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="analytics-loading">
        <div className="h-10 bg-[#F3F4EE] rounded-md animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-[#B93838]" data-testid="analytics-error">
        {error || 'No analytics data'}
      </div>
    );
  }

  const { summary, daily, monthly, by_salesperson, by_category, top_customers } = data;
  const topSalespeople = by_salesperson.slice(0, 10);

  // Build a quick {month: {count, value}} lookup so the compare card can
  // look up the two selected months in O(1) and compute deltas.
  const monthlyMap = {};
  monthly.forEach(m => { monthlyMap[m.month] = m; });
  const mA = monthlyMap[monthA] || { count: 0, value: 0 };
  const mB = monthlyMap[monthB] || { count: 0, value: 0 };
  const avgA = mA.count > 0 ? mA.value / mA.count : 0;
  const avgB = mB.count > 0 ? mB.value / mB.count : 0;
  const cmpCount = pctChange(mA.count, mB.count);
  const cmpValue = pctChange(mA.value, mB.value);
  const cmpAvg = pctChange(avgA, avgB);

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-[#1A1C16]">Analytics</h2>
          <p className="text-xs text-[#5C6150]">
            {data.date_range?.applied
              ? <>Showing <span className="font-semibold text-[#556B2F]">{data.date_range.start || '…'} → {data.date_range.end || '…'}</span></>
              : 'System-wide and per-salesperson performance (all-time)'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter-127 — Comparison Analytics opens as its own full page for
              meetings-ready deep dives (period picker, pie charts, per-
              salesperson head-to-head, presentation mode). */}
          <Button
            onClick={() => navigate('/admin/comparison-analytics')}
            className="bg-gradient-to-r from-[#DAA520] to-[#F1CB6B] text-[#5C4B14] hover:from-[#B8891A] hover:to-[#DAA520] hover:text-white shadow-sm border border-[#DAA520]/60"
            data-testid="open-comparison-analytics-btn"
          >
            <GitCompareArrows className="w-4 h-4 mr-1.5" />
            Comparison Analytics
          </Button>
          <p className="text-[10px] text-[#8F9485] w-full sm:w-auto sm:ml-1">Across {summary.total_users} users · {summary.total_quotations} quotations{data.date_range?.applied ? ' in range' : ''}</p>
        </div>
      </div>

      {/* Iter-127 — Comparison Analytics moved to its own full-page route
          (`/admin/comparison-analytics`). Classic Analytics is preserved
          exactly as it was so owner can read the numbers at a glance
          without dealing with A/B period picker + delta chips. Deep
          dive lives on its own page for meetings. */}
      {/* Date range picker */}
      <DateRangeChips
        preset={rangePreset}
        startDate={startDate}
        endDate={endDate}
        onPreset={applyPreset}
        onStartDate={(v) => { setRangePreset('custom'); setStartDate(v); }}
        onEndDate={(v) => { setRangePreset('custom'); setEndDate(v); }}
      />

      {/* Stat cards — 4 across on desktop, 2 across on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={FileText}
          label="Total Quotations"
          value={summary.total_quotations.toLocaleString('en-IN')}
          sub={data.date_range?.applied ? 'in selected range' : `${summary.this_month_count} this month`}
          accent="bg-[#556B2F]"
        />
        <StatCard
          icon={IndianRupee}
          label="Total Value"
          value={short(summary.total_value)}
          sub={`Avg ${short(summary.avg_value)}`}
          accent="bg-[#DAA520]"
        />
        <StatCard
          icon={Truck}
          label="Conversion Rate"
          value={`${summary.conversion_rate}%`}
          sub={`${summary.delivered_count} closed`}
          accent="bg-[#2E7D32]"
        />
        <StatCard
          icon={TrendingUp}
          label="Avg Days to Close"
          value={summary.avg_days_to_close != null ? summary.avg_days_to_close.toString() : '—'}
          sub="closed in last 90 days"
          accent="bg-[#4A90A4]"
        />
      </div>

      {/* Order Status card — moved to top of analytics per owner priority.
          Closed / Pending / Lost + lost reasons is the KPI the admin looks
          at first, so it lives right below the stat cards. */}
      <OrderStatusOverviewCard data={data} />

      {/* Daily line chart — last 30 days */}
      <Card className="border-[#E2E6D9]" data-testid="chart-daily">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#556B2F]" /> Last 30 Days — Quotations &amp; Value
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10, fill: '#5C6150' }} interval={2} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#5C6150' }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={short} tick={{ fontSize: 10, fill: '#5C6150' }} />
                <Tooltip
                  formatter={(val, name) => name === 'value' ? short(val) : val}
                  labelFormatter={dayLabel}
                  contentStyle={{ fontSize: '12px', borderRadius: '6px', borderColor: '#E2E6D9' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line yAxisId="left" type="monotone" dataKey="count" name="Quotations" stroke="#556B2F" strokeWidth={2} dot={{ r: 2 }} />
                <Line yAxisId="right" type="monotone" dataKey="value" name="Value" stroke="#DAA520" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Month-vs-month comparison card */}
      <Card className="border-[#E2E6D9]" data-testid="card-month-compare">
        <CardHeader className="py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
              <GitCompareArrows className="w-4 h-4 text-[#DAA520]" /> Compare Two Months
            </CardTitle>
            <div className="flex items-center gap-2 text-xs flex-wrap sm:flex-nowrap">
              <Select value={monthA} onValueChange={setMonthA}>
                <SelectTrigger className="h-8 w-28 sm:w-32 text-xs border-[#E2E6D9]" data-testid="month-a-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthly.slice().reverse().map(m => (
                    <SelectItem key={`A-${m.month}`} value={m.month}>{monthFullLabel(m.month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[#8F9485]">vs</span>
              <Select value={monthB} onValueChange={setMonthB}>
                <SelectTrigger className="h-8 w-28 sm:w-32 text-xs border-[#E2E6D9]" data-testid="month-b-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthly.slice().reverse().map(m => (
                    <SelectItem key={`B-${m.month}`} value={m.month}>{monthFullLabel(m.month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <CompareTile
              label="Quotations"
              valueA={mA.count.toLocaleString('en-IN')}
              valueB={mB.count.toLocaleString('en-IN')}
              cmp={cmpCount}
              testId="cmp-quotations"
            />
            <CompareTile
              label="Total Value"
              valueA={short(mA.value)}
              valueB={short(mB.value)}
              cmp={cmpValue}
              testId="cmp-value"
            />
            <CompareTile
              label="Avg / Quote"
              valueA={short(avgA)}
              valueB={short(avgB)}
              cmp={cmpAvg}
              testId="cmp-avg"
            />
          </div>
          <p className="text-[10px] text-[#8F9485] text-center mt-3">
            <span className="font-semibold text-[#1A1C16]">{monthFullLabel(monthA)}</span> compared against <span className="font-semibold text-[#1A1C16]">{monthFullLabel(monthB)}</span>
          </p>
        </CardContent>
      </Card>

      {/* Two-column row — monthly bar + category donut */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#E2E6D9]" data-testid="chart-monthly">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wide">Monthly Trend (12m)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fill: '#5C6150' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#5C6150' }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={monthLabel}
                    formatter={(val, name) => name === 'value' ? short(val) : val}
                    contentStyle={{ fontSize: '12px', borderRadius: '6px', borderColor: '#E2E6D9' }}
                  />
                  <Bar dataKey="count" name="Quotations" fill="#556B2F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E6D9]" data-testid="chart-category">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
              <Boxes className="w-4 h-4 text-[#DAA520]" /> Value Share by Category
              <span className="text-[10px] font-normal text-[#8F9485] normal-case tracking-normal ml-1">(hover / tap a slice)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <AnimatedCategoryPie data={by_category} testId="classic-category-pie" />
          </CardContent>
        </Card>
      </div>

      {/* Top salespeople — horizontal bar */}
      <Card className="border-[#E2E6D9]" data-testid="chart-salespeople">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#DAA520]" /> Top Salespeople by Value
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {topSalespeople.length === 0 ? (
            <p className="text-sm text-[#8F9485] py-4">No data yet — quotations will populate this once created.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSalespeople} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
                  <XAxis type="number" tickFormatter={short} tick={{ fontSize: 10, fill: '#5C6150' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#1A1C16' }} width={120} />
                  <Tooltip
                    formatter={(val, name) => name === 'value' ? short(val) : val}
                    contentStyle={{ fontSize: '12px', borderRadius: '6px', borderColor: '#E2E6D9' }}
                  />
                  <Bar dataKey="value" name="Value" fill="#556B2F" radius={[0, 4, 4, 0]} label={{ position: 'right', formatter: short, fontSize: 10, fill: '#5C6150' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salesperson detail table — full breakdown with click-to-expand
          drill-down. Clicking a row reveals the last 5 quotations by that
          salesperson (scoped to the current date-range filter) plus a
          "View all" link that jumps to Quotation History pre-filtered. */}
      <SalespersonBreakdownTable
        rows={by_salesperson}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Top customers */}
      <Card className="border-[#E2E6D9]" data-testid="table-customers">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wide">Top 10 Customers</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm min-w-[440px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C6150] border-b border-[#E2E6D9]">
                  <th className="py-2 px-3">#</th>
                  <th className="py-2 px-3">Customer</th>
                  <th className="py-2 px-3">Mobile</th>
                  <th className="py-2 px-3 text-right">Quotations</th>
                  <th className="py-2 px-3 text-right">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {top_customers.length === 0 ? (
                  <tr><td colSpan="5" className="py-6 text-center text-[#8F9485]">No customers yet</td></tr>
                ) : top_customers.map((c, i) => (
                  <tr key={`${c.mobile}-${i}`} className="border-b border-[#E2E6D9] hover:bg-[#F9FAF7]">
                    <td className="py-2 px-3 text-[#8F9485]">{i + 1}</td>
                    <td className="py-2 px-3 font-medium text-[#1A1C16]">{c.name}</td>
                    <td className="py-2 px-3 font-mono-data text-[#5C6150]">{c.mobile || '—'}</td>
                    <td className="py-2 px-3 text-right font-mono-data">{c.count}</td>
                    <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(c.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Month-vs-Month kept as legacy widget below the main comparison view.
          Referrer (architects/carpenters) and PurchaseOrder analytics kept
          in the classic analytics view — they were only excluded from the
          new comparison view per iter-124 owner directive. */}
      <MonthCompareCard data={data} />
      <ReferrerAnalyticsCard />
      <PurchaseOrderAnalyticsCard />
    </div>
  );
}

// =====================================================================
// Order Status Overview — Closed / Pending / Lost summary, per-staff
// breakdown, and lost-reasons table. Data comes from the existing
// /admin/analytics payload (status_breakdown, by_salesperson with
// closed_count/pending_count/lost_count, lost_reasons).
// =====================================================================
function OrderStatusOverviewCard({ data }) {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [themes, setThemes] = useState('');
  const [themesLoading, setThemesLoading] = useState(false);
  const lostReasons = data?.lost_reasons || [];

  // Fetch the AI-clustered themes once there are enough lost reasons to
  // make clustering worthwhile (3+). Falls back to raw list when LLM
  // unavailable — handled server-side.
  useEffect(() => {
    if (!data || lostReasons.length < 3) { setThemes(''); return; }
    setThemesLoading(true);
    api.getLostReasonsSummary()
      .then(r => setThemes(r.data?.themes || ''))
      .catch(() => setThemes(''))
      .finally(() => setThemesLoading(false));
  }, [data, lostReasons.length]);

  if (!data) return null;
  const sb = data.status_breakdown || { counts: { closed: 0, pending: 0, lost: 0 }, values: { closed: 0, pending: 0, lost: 0 }, total: 0 };
  const staffRows = (data.by_salesperson || []).filter(r => (r.closed_count || 0) + (r.pending_count || 0) + (r.lost_count || 0) > 0);

  const total = Math.max(1, sb.total);
  const pieData = [
    { name: 'Closed', value: sb.counts.closed, color: '#2E7D32' },
    { name: 'Pending', value: sb.counts.pending, color: '#F9A825' },
    { name: 'Lost', value: sb.counts.lost, color: '#C62828' },
  ];

  const closedPct = ((sb.counts.closed / total) * 100).toFixed(0);
  const lostPct = ((sb.counts.lost / total) * 100).toFixed(0);

  return (
    <Card className="border-[#E2E6D9]" data-testid="status-overview-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-[#1A1C16]">
          <TrendingUp className="w-4 h-4 text-[#556B2F]" /> Order Status — Closed / Pending / Lost
        </CardTitle>
        <p className="text-[11px] text-[#8F9485] mt-1" data-testid="status-overview-summary">
          {sb.total} quotations · {closedPct}% closed · {lostPct}% lost
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg border-2 border-[#2E7D32] p-3 bg-[#2E7D32]/5" data-testid="status-card-closed">
            <p className="text-[10px] uppercase tracking-wide text-[#2E7D32] font-semibold">Closed</p>
            <p className="text-2xl font-bold text-[#1A1C16] mt-0.5">{sb.counts.closed}</p>
            <p className="text-[10px] text-[#5C6150]">{formatINR(sb.values.closed)}</p>
          </div>
          <div className="rounded-lg border-2 border-[#F9A825] p-3 bg-[#F9A825]/5" data-testid="status-card-pending">
            <p className="text-[10px] uppercase tracking-wide text-[#F9A825] font-semibold">Pending</p>
            <p className="text-2xl font-bold text-[#1A1C16] mt-0.5">{sb.counts.pending}</p>
            <p className="text-[10px] text-[#5C6150]">{formatINR(sb.values.pending)}</p>
          </div>
          <div className="rounded-lg border-2 border-[#C62828] p-3 bg-[#C62828]/5" data-testid="status-card-lost">
            <p className="text-[10px] uppercase tracking-wide text-[#C62828] font-semibold">Lost</p>
            <p className="text-2xl font-bold text-[#1A1C16] mt-0.5">{sb.counts.lost}</p>
            <p className="text-[10px] text-[#5C6150]">{formatINR(sb.values.lost)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Donut chart */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Per-staff table */}
          <div className="overflow-auto max-h-60 border border-[#E2E6D9] rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-[#F9FAF7] sticky top-0">
                <tr>
                  <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Staff</th>
                  <th className="text-right py-1.5 px-1 text-[#2E7D32]">Closed</th>
                  <th className="text-right py-1.5 px-1 text-[#F9A825]">Pending</th>
                  <th className="text-right py-1.5 px-1 text-[#C62828]">Lost</th>
                </tr>
              </thead>
              <tbody>
                {staffRows.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-6 text-[#8F9485]">No staff status data yet.</td></tr>
                ) : staffRows.map(r => (
                  <tr
                    key={r.user_id}
                    onClick={() => setSelectedStaff(selectedStaff === r.user_id ? null : r.user_id)}
                    className={`border-t border-[#E2E6D9] hover:bg-[#F9FAF7] cursor-pointer ${selectedStaff === r.user_id ? 'bg-[#FFFBF0]' : ''}`}
                    data-testid={`status-staff-row-${r.user_id}`}
                  >
                    <td className="py-1.5 px-2 font-medium text-[#1A1C16]">{r.name || 'Unknown'}</td>
                    <td className="py-1.5 px-1 text-right font-mono-data">{r.closed_count}</td>
                    <td className="py-1.5 px-1 text-right font-mono-data">{r.pending_count}</td>
                    <td className="py-1.5 px-1 text-right font-mono-data">{r.lost_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lost reasons table — appears when there are any */}
        {lostReasons.length > 0 && (
          <div className="mt-4">
            {/* AI-clustered themes — Claude groups variants like
                "price too high" / "very expensive" / "cost issue" into
                top 2-3 themes. Hidden until at least 3 reasons exist. */}
            {(themes || themesLoading) && (
              <div className="mb-3 p-3 rounded-md border border-[#DAA520]/40 bg-[#FFFBF0]" data-testid="lost-themes-summary">
                <p className="text-[10px] uppercase tracking-wide text-[#8B6914] mb-1">
                  Top themes ({themesLoading ? 'AI summarising…' : 'AI clustered'})
                </p>
                <p className="text-xs text-[#5C6150] whitespace-pre-line">{themes || 'Working on it…'}</p>
              </div>
            )}
            <p className="text-xs font-bold uppercase tracking-wide text-[#C62828] mb-2">
              Lost Reasons {selectedStaff ? `(filtered to selected staff)` : `(${lostReasons.length})`}
            </p>
            <div className="overflow-auto max-h-64 border border-[#E2E6D9] rounded-md" data-testid="lost-reasons-table">
              <table className="w-full text-xs">
                <thead className="bg-[#F9FAF7] sticky top-0">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Staff</th>
                    <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Customer</th>
                    <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Reason</th>
                    <th className="text-right py-1.5 px-2 font-semibold text-[#5C6150]">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {lostReasons
                    .filter(r => !selectedStaff || (data.by_salesperson || []).find(s => s.user_id === selectedStaff)?.name === r.staff_name)
                    .map((r, i) => (
                    <tr key={i} className="border-t border-[#E2E6D9] hover:bg-[#F9FAF7]">
                      <td className="py-1.5 px-2 text-[#1A1C16]">{r.staff_name}</td>
                      <td className="py-1.5 px-2 text-[#1A1C16]">{r.customer_name}{r.customer_mobile ? ` · ${r.customer_mobile}` : ''}</td>
                      <td className="py-1.5 px-2 text-[#5C6150] italic">&ldquo;{r.reason}&rdquo;</td>
                      <td className="py-1.5 px-2 text-right font-mono-data text-[#C62828]">{formatINR(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedStaff && (
              <p className="text-[10px] text-[#8F9485] mt-1">Click staff row again to clear filter.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Day-by-day This Month vs Last Month line chart
// =====================================================================
function MonthCompareCard({ data }) {
  if (!data?.month_compare) return null;
  const series = data.month_compare;
  const s = data.month_compare_summary || {};
  return (
    <Card className="border-[#E2E6D9]" data-testid="month-compare-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-[#1A1C16]">
          <Calendar className="w-4 h-4 text-[#DAA520]" /> This Month vs Last Month — Day by Day
        </CardTitle>
        <p className="text-[11px] text-[#8F9485] mt-1" data-testid="month-compare-summary">
          <span className="text-[#556B2F] font-semibold">{s.this_label}</span>: {s.this_total_count} quotes · {formatINR(s.this_total_value)} ·{' '}
          <span className="text-[#8B4513] font-semibold">{s.last_label}</span>: {s.last_total_count} quotes · {formatINR(s.last_total_value)}
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#8F9485" />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#556B2F" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#DAA520" tickFormatter={short} />
              <Tooltip formatter={(v, n) => n.includes('value') ? formatINR(v) : v} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line yAxisId="left" type="monotone" dataKey="this_count" name={`${s.this_label} count`} stroke="#556B2F" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="left" type="monotone" dataKey="last_count" name={`${s.last_label} count`} stroke="#8B4513" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="this_value" name={`${s.this_label} value`} stroke="#DAA520" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="last_value" name={`${s.last_label} value`} stroke="#C9A961" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Referrer Analytics (Carpenters & Architects)
// Optional fields captured on each quotation; admins use this to see
// which referrers are driving business and which customers came in
// through them.
// =====================================================================
function ReferrerAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({ carpenter: null, architect: null });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getReferrerAnalytics({ search })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load referrer analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  const renderList = (kind, items) => (
    <div className="space-y-1">
      {items.length === 0 ? (
        <p className="text-xs text-[#8F9485] py-4 text-center">
          No {kind === 'carpenter' ? 'carpenters' : 'architects'} {search ? `match "${search}"` : 'yet'}.
          {!search && ` Add ${kind === 'carpenter' ? 'a carpenter' : 'an architect'} name on a quotation to start tracking.`}
        </p>
      ) : items.map((r, i) => {
        const isOpen = expanded[kind] === r.name;
        return (
          <div key={r.name} className="border border-[#E2E6D9] rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setExpanded(e => ({ ...e, [kind]: isOpen ? null : r.name }))}
              className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-[#F9FAF7]"
              data-testid={`${kind}-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[#8F9485] font-mono w-5 text-right text-xs">{i + 1}</span>
                <span className="font-medium text-[#1A1C16] truncate">{r.name}</span>
                <Badge variant="outline" className="text-[10px] py-0">{r.count} {r.count === 1 ? 'quote' : 'quotes'}</Badge>
              </div>
              <span className="font-mono-data text-[#556B2F] font-semibold text-sm whitespace-nowrap">{formatINR(r.value)}</span>
            </button>
            {isOpen && (
              <div className="px-3 py-2 bg-[#F9FAF7] border-t border-[#E2E6D9] text-xs">
                <p className="text-[#5C6150] mb-1 uppercase tracking-wide text-[10px]">Customers ({r.customers.length})</p>
                <div className="space-y-0.5">
                  {r.customers.map((c, ci) => (
                    <div key={ci} className="flex items-center justify-between">
                      <span className="truncate text-[#1A1C16]">{c.name}{c.mobile ? ` · ${c.mobile}` : ''}</span>
                      <span className="font-mono-data text-[#556B2F] whitespace-nowrap ml-2">{c.count}× · {formatINR(c.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Card className="border-[#E2E6D9]" data-testid="referrer-analytics-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base text-[#1A1C16]">
            <Users className="w-4 h-4 text-[#DAA520]" /> Carpenter & Architect Referrers
          </CardTitle>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search referrer name…"
            className="h-8 text-sm border border-[#E2E6D9] rounded-md px-2 bg-white w-full sm:w-56"
            data-testid="referrer-search-input"
          />
        </div>
        {data?.totals && (
          <p className="text-[11px] text-[#8F9485] mt-1">
            {data.totals.carpenter_count} carpenters · {data.totals.architect_count} architects · referred {formatINR((data.totals.carpenter_value || 0) + (data.totals.architect_value || 0))} in business
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && <Skeleton className="h-24 w-full" />}
        {!loading && error && <p className="text-sm text-[#B93838]">{error}</p>}
        {!loading && !error && data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#556B2F] mb-2">Carpenters</p>
              {/* Scroll-cap: keeps the dashboard from stretching when
                  many referrers have been logged. Internal scroll only. */}
              <div className="max-h-80 overflow-y-auto pr-1" data-testid="carpenter-list-scroll">
                {renderList('carpenter', data.carpenters || [])}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-[#8B4513] mb-2">Architects</p>
              <div className="max-h-80 overflow-y-auto pr-1" data-testid="architect-list-scroll">
                {renderList('architect', data.architects || [])}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Purchase Order Analytics
// Total POs, total value, total items ordered + tables by supplier,
// brand, and product.
// =====================================================================
function PurchaseOrderAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('supplier');

  useEffect(() => {
    let cancelled = false;
    api.getPOAnalytics()
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load PO analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const TABS = [
    { value: 'supplier', label: 'By Supplier', key: 'by_supplier' },
    { value: 'brand', label: 'By Brand', key: 'by_brand' },
    { value: 'product', label: 'By Product', key: 'by_product' },
  ];

  const activeKey = TABS.find(t => t.value === tab)?.key || 'by_supplier';
  const rows = data?.[activeKey] || [];

  return (
    <Card className="border-[#E2E6D9]" data-testid="po-analytics-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-[#1A1C16]">
          <Boxes className="w-4 h-4 text-[#1F4E79]" /> Purchase Order Analytics
        </CardTitle>
        {data?.summary && (
          <p className="text-[11px] text-[#8F9485] mt-1" data-testid="po-summary-line">
            {data.summary.po_count} POs · {data.summary.total_items_qty.toLocaleString('en-IN')} items ordered · {formatINR(data.summary.total_value)}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && <Skeleton className="h-24 w-full" />}
        {!loading && error && <p className="text-sm text-[#B93838]">{error}</p>}
        {!loading && !error && data && (
          <>
            <div className="flex gap-1 mb-3 flex-wrap" role="tablist">
              {TABS.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTab(t.value)}
                  className={`text-xs px-3 py-1 rounded-md border transition-colors ${
                    tab === t.value
                      ? 'bg-[#556B2F] text-white border-[#556B2F]'
                      : 'bg-white text-[#5C6150] border-[#E2E6D9] hover:bg-[#F9FAF7]'
                  }`}
                  data-testid={`po-tab-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-[#8F9485] border-b border-[#E2E6D9]">
                  <tr>
                    <th className="py-2 px-2 text-left">#</th>
                    <th className="py-2 px-2 text-left">{tab === 'supplier' ? 'Supplier' : tab === 'brand' ? 'Brand' : 'Product'}</th>
                    <th className="py-2 px-2 text-right">POs</th>
                    <th className="py-2 px-2 text-right">Qty</th>
                    <th className="py-2 px-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="5" className="py-6 text-center text-[#8F9485]">No purchase orders yet.</td></tr>
                  ) : rows.slice(0, 20).map((r, i) => (
                    <tr key={i} className="border-b border-[#E2E6D9] hover:bg-[#F9FAF7]">
                      <td className="py-2 px-2 text-[#8F9485]">{i + 1}</td>
                      <td className="py-2 px-2 font-medium text-[#1A1C16]">
                        {tab === 'supplier' ? r.supplier_name : tab === 'brand' ? r.brand_name : (<><span>{r.brand_name}</span> <span className="text-[#8F9485]">· {r.name}</span></>)}
                      </td>
                      <td className="py-2 px-2 text-right font-mono-data">{r.po_count}</td>
                      <td className="py-2 px-2 text-right font-mono-data">{Number(r.total_qty || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2 px-2 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(r.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Daily PO line chart — last 30 days */}
            {data.daily && data.daily.length > 0 && (
              <div className="mt-4 h-44">
                <p className="text-[10px] uppercase tracking-wide text-[#8F9485] mb-1">Last 30 days — PO count</p>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E6D9" />
                    <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 10 }} stroke="#8F9485" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#556B2F" allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" name="POs" stroke="#1F4E79" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Recent POs — flat table with date + PO number so admins can
                cross-check against the stock register. Limited to last 20
                for the dashboard view (full list is on the POs page). */}
            {data.recent_pos && data.recent_pos.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wide text-[#8F9485] mb-2">Recent Purchase Orders</p>
                <div className="overflow-auto max-h-64 border border-[#E2E6D9] rounded-md" data-testid="recent-pos-table">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F9FAF7] sticky top-0">
                      <tr>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">PO #</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Supplier</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Date</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-[#5C6150]">Items</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-[#5C6150]">Qty</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#5C6150]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_pos.slice(0, 20).map(p => (
                        <tr key={p.id} className="border-t border-[#E2E6D9] hover:bg-[#F9FAF7]">
                          <td className="py-1.5 px-2 font-mono text-[#556B2F]">{p.po_number || p.id.slice(0, 8)}</td>
                          <td className="py-1.5 px-2 text-[#1A1C16]">{p.supplier_name || '—'}</td>
                          <td className="py-1.5 px-2 text-[#5C6150]">{p.po_date || (p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—')}</td>
                          <td className="py-1.5 px-2 text-right font-mono-data">{p.items_count}</td>
                          <td className="py-1.5 px-2 text-right font-mono-data">{Number(p.items_qty || 0).toLocaleString('en-IN')}</td>
                          <td className="py-1.5 px-2 text-[#5C6150] uppercase text-[10px]">{p.status || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
